import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnInit, Output, ViewChild } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { HttpErrorResponse } from '@angular/common/http';
import { LoaderService } from '../../../services/loader.service';
import { ToastService } from '../../../services/toast.service';
import { AuthService } from '../../../services/auth.service';
import { FormsModule } from '@angular/forms';
import { NgOtpInputComponent, NgOtpInputConfig } from 'ng-otp-input';
import { CdkCopyToClipboard } from "@angular/cdk/clipboard";
import { OTP_CONFIG_6_DIGIT, OTP_CONFIG_8_DIGIT } from '../../../services/otp-config';

export interface TwoFactorAuthAPIResponse {
  status_code: number;
  message: string;
  data: {
    qr_code_base64: string;
    manual_key: string;
    two_factor_recovery_code: string;
    jwt_token: string;
    type: string;
  }
}

@Component({
  selector: 'app-authenticator-modal',
  imports: [CommonModule, FormsModule, NgOtpInputComponent],
  templateUrl: './authenticator-modal.component.html',
  styleUrl: './authenticator-modal.component.css'
})
export class AuthenticatorModalComponent implements OnInit {
  @Output() close = new EventEmitter<void>();
  step: 1 | 2 | 3 | 4 | 5 | 6 = 1;
  otp: string = '';
  mfaResp: any = {};
  userData: any;
  isDisableCall: boolean = false;
  isCopied = false;
  otpConfig = OTP_CONFIG_6_DIGIT
  recoveryOtpConfig = OTP_CONFIG_8_DIGIT
  constructor(
    private auth: AuthService,
    private toastr: ToastService,
    private loader: LoaderService,
    private service: ApiService
  ) { }

  ngOnInit(): void {
    this.userData = this.auth.getUserData();
    if (this.userData.enable_two_factor === true) {
      this.step = 3;
      this.isDisableCall = true;
    }
  }

  nextStep() {
    if (this.step < 5) {
      this.step++;
    }
  }

  finishSetup() {
    this.close.emit();
  }

  /**
   * OTP change handler
   * @param otp 
   */
  onOtpChange(otp: string) {
    this.otp = otp;
    if (this.otp.length === 6 && this.step === 3) {
      if (!this.isDisableCall) {
        this.verifyMFACode();
      } else {
        this.disableMFA();
      }
    } else if (this.otp.length === 8 && this.step === 6) {
      this.disableWithRecovery();
    }
  }

  /**
   * Copy recovery code
   * @param data 
   * @returns 
   */
  copyRecoveryCode(data: string): void {
    if (!data) return;

    navigator.clipboard.writeText(data).then(() => {
      this.isCopied = true;

      // reset back to normal after 2 seconds
      setTimeout(() => {
        this.isCopied = false;
      }, 2000);
    });
  }

  initiateMFA(): void {
    this.loader.show();
    this.service
      .enable2FA()
      .subscribe((result: TwoFactorAuthAPIResponse) => {
        let data = result.data;
        data.qr_code_base64 = "data:image/png;base64, " + data.qr_code_base64;
        this.mfaResp = data;
        console.log("this.mfaResp", this.mfaResp);
        this.auth.setTempSessionTOken(data.jwt_token);
        this.loader.hide();
        this.step = 2;
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.loader.hide();
      });
  }

  verifyMFACode(): void {
    if (this.otp.length !== 6) {
      this.toastr.error("Please enter a valid 6-digit code");
      return;
    }
    this.loader.show();
    const skipTfatoken = false;
    this.service
      .verify2FA(this.otp, skipTfatoken)
      .subscribe((result: any) => {
        this.auth.removeTempSessionToken();
        this.userData.enable_two_factor = true;
        this.auth.setUserData(this.userData);
        this.loader.hide();
        this.step = 4;
      }, (error: HttpErrorResponse) => {
        console.log(error);
        let message: string = error?.error?.error_message || error.error;
        this.toastr.error(message);
        this.loader.hide();
      });
  }

  /**
   * Disable TFA
   */
  disableMFA(): void {
    if (this.otp.length !== 6) {
      this.toastr.error("Please enter a valid 6-digit code");
      return;
    }
    this.loader.show();
    this.service
      .disable2FA(this.otp)
      .subscribe((result: any) => {
        this.userData.enable_two_factor = false;
        this.auth.setUserData(this.userData);
        this.toastr.success("TFA disabled successfully.");
        this.loader.hide();
        this.step = 5;
      }, (error: HttpErrorResponse) => {
        let message: string = error?.error?.error_message || error.error;
        this.toastr.error(message);
        this.loader.hide();
      });
  }

  /**
   * Disable TFA with recovery code
   * @returns 
   */
  disableWithRecovery(): void {
    if (this.otp.length !== 8) {
      this.toastr.error("Please enter a valid 8-digit recovery code");
      return;
    }
    this.loader.show();
    this.service
      .remove2FA(this.otp)
      .subscribe((result: any) => {
        this.userData.enable_two_factor = false;
        this.auth.setUserData(this.userData);
        this.toastr.success("TFA disabled successfully.");
        this.loader.hide();
        this.step = 5;
      }, (error: HttpErrorResponse) => {
        let message: string = error?.error?.error_message || error.error;
        this.toastr.error(message);
        this.loader.hide();
      });
  }

  openRecovery() {
    this.step = 6;
    this.otp = ''; // Reset OTP for recovery code entry
  }

  closeRecovery() {
    this.step = 3;
    this.otp = '';
  }

  closeModal() {
    this.auth.reUpdateProfile();
    this.close.emit();
  }

}